/*
 *  -----------------------------------------------------------------------
 *    <copyright file="study-risk-assessment-setup.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React from 'react';
import { boolToYesNo } from '@components/admin/studies/study-cm-setup/study-cm-setup.helper';
import OutlinedWrapper from '@components/core/outlined-wrapper';
import { useTranslation } from 'hooks';
import { StudyFields } from 'queries/generated/fragments.generated';
import 'views/components/admin/studies/admin-study.scss';

const StudyRiskAssessmentSetup = ({ study }: { study: StudyFields }) => {
    const { t } = useTranslation();
    const { t: tCtqWidgets } = useTranslation('ram', { keyPrefix: 'ctqWidgets' });

    const selectedModel = study.organisation.evaluationModels.find((model: any) => {
        if (model.id === study.evaluationModelId) return model;
        return null;
    });

    const LibraryCategories = study.riskLibraryCategories.map((category: any) =>
        study.riskLibraryCategoryIds.map((catId: string) => {
            if (category.id === catId)
                return (
                    <span key={category.id} className="value">
                        {category.name}
                    </span>
                );
            return null;
        }),
    );

    return (
        <div className="study-settings-wrapper">
            <div className="d-flex">
                <div className="data-wrap">
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:evaluationModel')}:</strong>
                        </span>
                        <span className="value">{selectedModel?.name || ''}</span>
                    </div>
                    <div className="row" style={{ marginBottom: '25px' }}>
                        <span className="title">
                            <strong>{t('admin:riskReviewWindow')}:</strong>
                        </span>
                        <span className="value">{study.riskReviewWindowDays}</span>
                    </div>
                    <OutlinedWrapper customClass="cm-setup-tr" label={tCtqWidgets('ctqfScreenWidgets')}>
                        <div className="row">
                            <span className="title">
                                <strong>{tCtqWidgets('numberTotal')}:</strong>
                            </span>
                            <span className="value">
                                {t(boolToYesNo(study.ctqScreenWidgetsConfiguration?.numberTotal ?? false))}
                            </span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{tCtqWidgets('numberActive')}:</strong>
                            </span>
                            <span className="value">
                                {t(boolToYesNo(study.ctqScreenWidgetsConfiguration?.numberActive ?? false))}
                            </span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{tCtqWidgets('numberActiveCritical')}:</strong>
                            </span>
                            <span className="value">
                                {t(boolToYesNo(study.ctqScreenWidgetsConfiguration?.numberActiveCritical ?? false))}
                            </span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{tCtqWidgets('numberActiveCtQsWithoutRisks')}:</strong>
                            </span>
                            <span className="value">
                                {t(
                                    boolToYesNo(
                                        study.ctqScreenWidgetsConfiguration?.numberActiveCtQsWithoutRisks ?? false,
                                    ),
                                )}
                            </span>
                        </div>
                        <div className="row">
                            <span className="title">
                                <strong>{tCtqWidgets('numberActiveCtQsWithoutFunctionalDataReviews')}:</strong>
                            </span>
                            <span className="value">
                                {t(
                                    boolToYesNo(
                                        study.ctqScreenWidgetsConfiguration
                                            ?.numberActiveCtQsWithoutFunctionalDataReviews ?? false,
                                    ),
                                )}
                            </span>
                        </div>
                    </OutlinedWrapper>
                </div>
                <div className="data-wrap">
                    <div className="row">
                        <span className="title">
                            <strong>{t('admin:riskLibraryCategoryLabel')}:</strong>
                        </span>
                        {LibraryCategories}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudyRiskAssessmentSetup;
